import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
    MatSlideToggle,
    MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs';

import {
    LineProgressStore,
    StopStore,
    VehicleStore,
} from 'src/app/modules/core/store';

import { MapStateService } from 'src/app/modules/map/map-state.service';
import { MapboxService } from 'src/app/modules/map/mapbox-map/mapbox-map.service';
import {
    StateOfChargeMeasurementPoint,
    Vehicle,
    VehicleActivity,
    VehicleBlockActivityInfo,
    VehiclePosition,
    VehicleWarningConfig,
    CurrentAssignment,
    AssignmentState,
} from 'src/app/modules/shared/models/vehicle';

import { BladeService } from 'src/app/modules/blades/services/blade.service';
import {
    AuthService,
    OMSContextSyncService,
} from 'src/app/modules/core/services';

import { USER_ROLES } from 'src/app/modules/core/services/sign-in.service';
import { VehicleInfoService } from 'src/app/modules/core/services/vehicle-info.service';
import { VehicleDeparture } from 'src/app/modules/shared/models/vehicle/vehicle-departure';
import { VehicleDepartureHistory } from 'src/app/modules/shared/models/vehicle/vehicle-departure-history';
import { VehicleWarning } from 'src/app/modules/shared/models/vehicle/vehicle-warning';
import { LoggingService } from 'src/app/modules/shared/services/logging.service';

import * as turf from '@turf/turf';
import * as _ from 'lodash';
import { MomentModule } from 'ngx-moment';
import { WarningMessagePipe } from '../../modules/shared/pipes/warning-message.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { SpinnerComponent } from '../../modules/shared/components/spinner/spinner.component';
import { StateOfChargeHistoryComponent } from './state-of-charge-history/state-of-charge-history.component';
import { DeviationTextComponent } from '../../modules/shared/components/deviation-text/deviation-text.component';
import { SpeedDotsComponent } from '../../modules/shared/components/speed-dots/speed-dots.component';
import { VehicleTripInfoComponent } from './vehicle-trip-info/vehicle-trip-info.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WarningIconComponent } from '../../modules/shared/components/warning-icon/warning-icon.component';
import { RestoreWarningComponent } from './restore-warning/restore-warning.component';
import { VehicleBlockInfoComponent } from './vehicle-block-info/vehicle-block-info.component';
import { MatDividerModule } from '@angular/material/divider';
import { TabsComponent } from '../../modules/shared/components/tabs/tabs.component';
import { BatteryStatusInfoComponent } from './battery-status-info/battery-status-info.component';
import { BladeHeaderComponent } from '../../modules/blades/components/blade-header/blade-header.component';
import { NgStyle, JsonPipe, DatePipe, AsyncPipe, NgFor } from '@angular/common';
import { AppStateService } from '@modules/core/services/app-state.service';
import { APP_CONTEXT } from '@modules/core/models/app-state';
import { VehicleService } from './vehicle.service';
import { TripCurrentStateEntry, WarningsService, SignOnFlowResult, SignOnOffStep } from 'src/app/modules/core/services/warning.service';
import { TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { VehicleTripInfoHelper } from './vehicle-trip-info/vehicle-trip-info.helper';
import moment from 'moment';
import { TrafficDeviationTripService } from '@components/traffic-deviations/services/traffic-deviation-trip.service';
import { TrafficDeviationAccessService } from '@modules/core/services/traffic-deviation-access.service';
import { SignOnWarningComponent } from './signon-warning/signon-warning.component';
import { ConfigService } from 'src/config';
import { WarningMessageType } from 'src/app/modules/shared/models';
import { LoggingHelperService } from '@modules/shared/helpers/logging-helper.service';
import { SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

export class VehicleComponentState {
    tabs: string[];
    selectedTab: string;
    inventoryNumber: string;
}

@Component({
    selector: 'app-vehicle',
    templateUrl: './vehicle.component.html',
    styleUrls: ['./vehicle.component.scss'],
    viewProviders: [VehicleService],
    imports: [
        BladeHeaderComponent,
        BatteryStatusInfoComponent,
        TabsComponent,
        MatDividerModule,
        VehicleBlockInfoComponent,
        RestoreWarningComponent,
        SignOnWarningComponent,
        WarningIconComponent,
        MatTooltipModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatIconModule,
        VehicleTripInfoComponent,
        NgStyle,
        NgFor,
        SpeedDotsComponent,
        DeviationTextComponent,
        StateOfChargeHistoryComponent,
        SpinnerComponent,
        JsonPipe,
        DatePipe,
        TranslateModule,
        WarningMessagePipe,
        MomentModule,
        AsyncPipe,
        SlicePipe,
        FormsModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class VehicleComponent implements OnInit, OnDestroy {
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _mapboxService = inject(MapboxService);
    private readonly _mapStateService = inject(MapStateService);
    private readonly _bladeService = inject(BladeService);
    private readonly _authService = inject(AuthService);
    private readonly _omsContext = inject(OMSContextSyncService);
    private readonly _vehicleInfoService = inject(VehicleInfoService);
    private readonly _logger = inject(LoggingService);
    private readonly _stopStore = inject(StopStore);
    private readonly _lineProgressStore = inject(LineProgressStore);
    private readonly _appState = inject(AppStateService);
    private readonly _trafficDeviationService = inject(
        TrafficDeviationTripService,
    );
    private readonly _vehicleService = inject(VehicleService);
    private readonly _warningsService = inject(WarningsService);
    private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
    private readonly _trafficDeviationAccessService = inject(
        TrafficDeviationAccessService,
    );
    private readonly _loggingService = inject(LoggingService);
    private readonly _configService = inject(ConfigService);
    private readonly _loggingHelperService = inject(LoggingHelperService);
    private _tripChangeRequestId = 0;

    bladeIndex: number;

    public title$: Observable<string | undefined>;
    public editRoles = [USER_ROLES.ADMIN];
    public inventoryNumber: string;
    public garageCode: string;
    public componentState: VehicleComponentState;
    public followVehicle = false;
    public vehicle: Vehicle;
    public readonly isConnectionFlow$: Observable<boolean>;

    public vehicleActivity$ = new BehaviorSubject<VehicleActivity>(undefined);
    public blockActivites: VehicleBlockActivityInfo[];
    public stateOfChargeHistory: StateOfChargeMeasurementPoint[];
    public departureHistory: VehicleDeparture[];

    public confirmableWarnings: VehicleWarning[] = [];
    public signOnWarnings: VehicleWarning[] = [];
    public warnings: VehicleWarning[] = [];
    public isTrafficDeviationEnabled = false;

    private _ngUnsubscribeVehicle$ = new Subject<void>();
    private _latestVehiclePosition: VehiclePosition;
    private readonly _sourceComponentName = 'VehicleComponent';
    private readonly _defaultPassengerInformationBaseUrl =
        'https://web.prod.cdri.sl.se/?screen=DOUBLE_VERTICAL/1&transport_authority=1&contractor=30&transport_mode=BUS&sendAck=0&v2=1';
    private readonly _title$ = new BehaviorSubject(undefined);
    private readonly _ngUnsubscribe$ = new Subject<void>();

    public signonForm = {
        signOff: false,
        signOffItem: {
            trafficDate: '',
            hastusregion: this.hastusRegion,
            routeName: '',
            tripNumber: '',
        },
        signOn: false,
        signOnItem: {
            trafficDate: '',
            hastusregion: this.hastusRegion,
            routeName: '',
            tripNumber: '',
            vehicleactivityid: '',
            stopId: '',
            sequenceNumber: '',
        },
        vehicletaskno: '',
    };


    public tripCurrentState: TripCurrentStateEntry | null = null;
    public signOffStep?: SignOnOffStep;
    public signOnStep?: SignOnOffStep;
    public isSubmittingSignOnUpdate = false;
    public checkingTripState = false;
    public trips: Array<{ route: string; tripNo: string; time: string; designation: string; vehicleactivityid: string }> = [];
    public stops: Array<{ id: string; name: string; sequenceNumber: string; departureTime: string }> = [];
    public selectedSignOnTrip:
        { route: string; tripNo: string; time: string; designation: string; vehicleactivityid: string }
        | undefined;
    public loadingStops = false;

    constructor() {
        this.title$ = this._title$.asObservable();
        this.isConnectionFlow$ =
            this._trafficDeviationService.isConnectionFlow$;
        // listen to router changes and then get the vehicle
        this._route.params
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe((params) => {
                this.inventoryNumber = params['inventoryNumber'];
                if (this.inventoryNumber) {
                    this.componentState = this._getVehicleComponentState();
                    this._initSubscriptions();
                    this._selectVehicle();
                } else {
                    this._resetVehicleSelection();
                }
            });
    }

    public get isSignOnFeatureEnabled(): boolean {
        return (
            this._configService.enableSignOnFeature ||
            this._authService.userHasAccessToSignOn()
        );
    }

    public get signonPrincipalLabel(): string {
        const garageCode = this.vehicle?.garageCode;
        if (!garageCode) return '';
        return this._configService.signonGarageCodePrincipalMap.get(garageCode) ?? '';
    }

    public get currentAssignment(): CurrentAssignment | undefined {
        return this.vehicle?.currentAssignment;
    }

    public readonly AssignmentState = AssignmentState;

    public get hastusRegion(): string {
        return this._appState.stateSnapshot.garages?.find(
            (g) => g.code === this.garageCode,
        )?.hastusRegionCode;
    }

    public get anotherVehicleIsSignedOn(): boolean {
        const signedOnVehicle = this.tripCurrentState?.vehicleId;
        const actualVehicle = this.vehicle?.inventoryNumber;

        return (
            this.tripCurrentState?.state === AssignmentState.SignedOn &&
            !!signedOnVehicle &&
            !!actualVehicle &&
            signedOnVehicle.trim().toLowerCase() !==
            actualVehicle.trim().toLowerCase()
        );
    }

    public get signOnUpdateStatus(): 'ok' | 'error' | null {
        const signOffStatus = this.signOffStep?.status?.trim().toLowerCase();
        const signOnStatus = this.signOnStep?.status?.trim().toLowerCase();

        if (signOffStatus === 'error' || signOnStatus === 'error') {
            return 'error';
        }

        if (
            ['ok', 'skipped'].includes(signOffStatus ?? '') &&
            ['ok', 'skipped'].includes(signOnStatus ?? '')
        ) {
            return 'ok';
        }

        return null;
    }

    canDeactivate(): boolean {
        return true;
    }

    ngOnInit() {
        this.componentState = this._getVehicleComponentState();
        this._updateElecticVehicleTabs();
        this._updateVehicleSignOnTabs();

        // Reset the followVehicleState if we are dragging in the map
        this._mapStateService
            .getMapPropertyState('dragging')
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe((dragging) => {
                if (dragging && this.followVehicle) {
                    this.followVehicle = false;
                }
            });

        this._loggingHelperService.logPageViewCount(
            'Vehicle',
            this._router.url,
        );
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
        this._resetDataOnVehicleChange();
        this._resetVehicleSelection();
        this._clearMap();
    }

    public get canOpenStreetView(): boolean {
        // Can only open if we are not running in OMS and we have a vehicle position
        return (
            !this._omsContext.runningInOMS &&
            this._latestVehiclePosition !== undefined
        );
    }

    public get isFirstBlade() {
        return this.bladeIndex !== undefined && this.bladeIndex === 0;
    }

    /** Determines which outlet to use when drilling into child content (e.g. activity). */
    public get drillInOutlet(): string {
        const outlet = this._route.outlet;
        switch (outlet) {
            case 'outlet-right0': return 'outlet-right1';
            case 'outlet-right1': return 'outlet-right2';
            default: return 'outlet2';
        }
    }

    public async changeTab(selectedTab: string) {
        this.componentState.selectedTab = selectedTab;
        if (selectedTab === 'signon') {
            this.signOffStep = undefined;
            this.signOnStep = undefined;
        }
        this._updateVehicleComponentState();

        this._loggingService.logEvent('User changed vehicle tab', {
            tab: selectedTab,
        });
    }

    public changeFollowVehicle(event: MatSlideToggle) {
        this.followVehicle = event.checked;
        this._tryFollowVehicle(this._latestVehiclePosition);

        this._loggingService.logEvent('User changed follow vehicle', {
            follow: event.checked,
        });
    }

    public openVehicleInStreetView() {
        if (!this._latestVehiclePosition) {
            return;
        }

        // https://developers.google.com/maps/documentation/urls/get-started#street-view-action

        const lat = this._latestVehiclePosition.latitude;
        const long = this._latestVehiclePosition.longitude;
        const heading = this._latestVehiclePosition.bearing;
        let url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${long}`;

        if (heading) {
            url += `&heading=${heading}`;
        }

        window.open(url, '_blank');

        this._loggingService.logEvent('User opened Street View', {
            inventoryNumber: this.inventoryNumber,
            url,
        });
    }

    public openPassengerInformation(inventoryNumber: string) {
        if (!inventoryNumber) {
            return;
        }

        const baseUrl =
            this._configService.passengerInformationBaseUrl ||
            this._defaultPassengerInformationBaseUrl;
        const encodedInventoryNumber = encodeURIComponent(inventoryNumber);
        const url = `${baseUrl}&vehicle=${encodedInventoryNumber}`;

        window.open(url, '_blank', 'width=800,height=600,resizable=yes');

        this._loggingService.logEvent('User opened passenger information', {
            inventoryNumber,
            url,
        });
    }

    public async openTrafficDeviation() {
        if (!this.vehicleActivity$.value) return;
        const vehicleActivity = this.vehicleActivity$.value;

        const trafficDeviationOutlet = this._router.url?.includes(
            'outlet0:traffic-deviation',
        );
        if (trafficDeviationOutlet) {
            this._router.navigate(['', { outlets: { outlet0: null } }]);
        }

        const { items: activityPointListItem } =
            VehicleTripInfoHelper.getActivityPointItems(
                false,
                vehicleActivity,
                this.vehicle?.departureDeviation,
                this.vehicle?.arrivalDeviation,
                this._appState,
            );
        this._trafficDeviationService.addVehicleActivity(
            new Vehicle({
                ...this.vehicle,
                routeNumber: vehicleActivity.routePublicName,
                tripNumber: vehicleActivity.tripNumber,
                trafficDate: vehicleActivity.trafficDate,
            }),
            activityPointListItem,
        );

        const currentContext = await firstValueFrom(
            this._appState.getPropertyState('selectedContext'),
        );
        if (currentContext !== APP_CONTEXT.NEW_TRAFFIC_DEVIATION) {
            this._appState.changeContext(APP_CONTEXT.NEW_TRAFFIC_DEVIATION);
        }

        const currentScope = await firstValueFrom(
            this._trafficDeviationsStore.deviationScope$,
        );
        if (currentScope !== 'TRIP') {
            const selectedDeviationType = 'ChangeOfJourneyTiming';
            this._trafficDeviationsStore.updateTrafficDeviationState({
                selectedDeviationType,
                deviationScope: 'TRIP',
            });
        }
    }

    public async addConnection() {
        const activityPoints = this._vehicleService.activityPointListItem.value;
        if (activityPoints?.length) {
            this._trafficDeviationService.addConnectionVehicleActivity(
                this.vehicle,
                this._vehicleService.activityPointListItem.value,
            );
        } else {
            const estimatedBlockActivity = this._getEstimatedBlockActivity();
            if (!estimatedBlockActivity?.vehicleActivityId) return;

            const vehicleActivity = await firstValueFrom(
                this._vehicleInfoService.getVehicleActivityById(
                    estimatedBlockActivity?.vehicleActivityId,
                ),
            );
            const { items: activityPointListItem } =
                VehicleTripInfoHelper.getActivityPointItems(
                    false,
                    vehicleActivity,
                    this.vehicle?.departureDeviation,
                    this.vehicle?.arrivalDeviation,
                    this._appState,
                );
            this._trafficDeviationService.addConnectionVehicleActivity(
                new Vehicle({
                    ...this.vehicle,
                    blockNumber: estimatedBlockActivity.omsBlockNumber,
                    routeNumber: vehicleActivity.routePublicName,
                    tripNumber: vehicleActivity.tripNumber,
                    trafficDate: vehicleActivity.trafficDate,
                }),
                activityPointListItem,
            );
        }
    }

    private _getEstimatedBlockActivity(): VehicleBlockActivityInfo {
        if (!this.blockActivites || this.vehicle.tripNumber) {
            return undefined;
        }

        const now = moment();

        // Find closest block by starttime and endtime
        const itemInTimeFrame = _.find(
            this.blockActivites.filter((a) => a.type === 'TRIPTRIP'),
            (va) => {
                const startTimeAsMoment = moment(va.startDateTime);
                const endTimeAsMoment = moment(va.endDateTime);
                return (
                    now.isAfter(startTimeAsMoment) &&
                    now.isBefore(endTimeAsMoment)
                );
            },
        );

        if (itemInTimeFrame) {
            return itemInTimeFrame;
        }

        // Find closest block by only starttime
        const allStartedBlockActivities = _.filter(
            this.blockActivites.filter((a) => a.type === 'TRIPTRIP'),
            (va) => {
                const startTimeAsMoment = moment(va.startDateTime);
                return startTimeAsMoment.isAfter(now);
            },
        );

        const itemWithStartClosestToNow = _.first(allStartedBlockActivities);
        if (itemWithStartClosestToNow) {
            return itemWithStartClosestToNow;
        }

        return undefined;
    }

    public closeRightBlade(): void {
        const outlet = this._route.outlet;
        const outlets: { [key: string]: any } = { [outlet]: null };
        // Cascade close: also close any deeper right outlets
        if (outlet === 'outlet-right0') {
            outlets['outlet-right1'] = null;
            outlets['outlet-right2'] = null;
        } else if (outlet === 'outlet-right1') {
            outlets['outlet-right2'] = null;
        }
        this._router.navigate(['', { outlets }]);
    }

    public get isSignonSubmitEnabled(): boolean {
        const signedOn = this.currentAssignment?.state === AssignmentState.SignedOn;

        const signOff = this.signonForm.signOff;
        const signOn = this.signonForm.signOn;

        if (signOff && !signOn) {
            return signedOn;
        }

        if (this.anotherVehicleIsSignedOn && this.signonForm.signOn) {
            return false;
        }

        if (signOn && !signOff) {
            if (this.currentAssignment?.routeName != null || this.currentAssignment?.tripNumber != null) {
                return false;
            }
            return !!this.signonForm.signOnItem.vehicleactivityid && !!this.signonForm.signOnItem.sequenceNumber;
        }

        if (signOff && signOn) {
            return signedOn && !!this.signonForm.signOnItem.vehicleactivityid && !!this.signonForm.signOnItem.sequenceNumber;
        }

        return false;
    }

    public onSignonCheckboxChange(
        key: 'signOff' | 'signOn',
        event: Event,
    ): void {
        const checked = (event.target as HTMLInputElement | null)?.checked ?? false;

        this.signOffStep = undefined;
        this.signOnStep = undefined;

        this.signonForm = {
            ...this.signonForm,
            [key]: checked,
        };

        if (!checked) {
            ++this._tripChangeRequestId;
            this.checkingTripState = false;
            this.loadingStops = false;
            this.tripCurrentState = null;
            this.selectedSignOnTrip = undefined;
            this.stops = [];
            this.signonForm.signOnItem.vehicleactivityid = '';
            this.signonForm.signOnItem.sequenceNumber = '';
            this.signonForm.signOnItem.stopId = '';
        }
    }

    public async onTripChange(): Promise<void> {
        const requestId = ++this._tripChangeRequestId;
        const isCurrentRequest = () => requestId === this._tripChangeRequestId;
        const selectedTrip = this.trips.find(
            (trip) =>
                trip.vehicleactivityid ===
                this.signonForm.signOnItem.vehicleactivityid
        );
        this.selectedSignOnTrip = selectedTrip;
        this.tripCurrentState = null;
        this.stops = [];
        this.signonForm.signOnItem.sequenceNumber = '';
        this.checkingTripState = false;
        this.loadingStops = false;
        this.signonForm.signOnItem.stopId = '';

        if (!selectedTrip) {
            return;
        }

        this.checkingTripState = true;

        try {
            const tripState = await this._warningsService.getTripCurrentState(
                this.hastusRegion,
                selectedTrip.route,
                selectedTrip.tripNo,
                this.vehicle?.trafficDate ?? '',
            );
            if (!isCurrentRequest()) {
                return;
            }
            this.tripCurrentState = tripState;
            this.checkingTripState = false;

            // Do not load stops when another vehicle is already signed on.
            if (this.anotherVehicleIsSignedOn) {
                return;
            }

            this.loadingStops = true;

            const response = await firstValueFrom(
                this._vehicleInfoService.getVehicleActivityById(
                    selectedTrip.vehicleactivityid,
                ),
            );

            if (!isCurrentRequest()) {
                return;
            }

            this.stops = (response?.activityPoints ?? []).map((stop) => ({
                id: String(stop.stopId ?? ''),
                name: String(stop.stopDescription ?? ''),
                sequenceNumber: String(stop.sequenceNumber ?? ''),
                departureTime: String(stop.departureTime ?? ''),
            }));

        } catch {
            if (isCurrentRequest()) {
                this.stops = [];
            }

        } finally {
            if (isCurrentRequest()) {
                this.checkingTripState = false;
                this.loadingStops = false;
            }
        }
    }

    protected trackByIndex(index: number): number {
        return index;
    }

    private _initSubscriptions() {
        this._resetDataOnVehicleChange();

        this._vehicleStore
            .getVehicleState(this.inventoryNumber)
            .pipe(takeUntil(this._ngUnsubscribeVehicle$))
            .subscribe(async (vehicle) => {
                this._resetDataOnVehicleChange();
                this.vehicle = vehicle;

                if (!vehicle) {
                    this.closeRightBlade();
                    return;
                }

                this.garageCode = vehicle.garageCode;

                const blockNumber = vehicle?.blockNumber?.trim();
                if (blockNumber && this.signonForm.vehicletaskno !== blockNumber) {
                    this.signonForm = {
                        ...this.signonForm,
                        vehicletaskno: blockNumber,
                    };
                }

                await this.refreshSignOnTrips();
                this._updateElecticVehicleTabs();
                this._updateVehicleSignOnTabs();
                this._title$.next(
                    vehicle?.inventoryNumberWithoutPrefix || undefined,
                );
                this._centerMapOnVehicle(vehicle?.position);
                this.isTrafficDeviationEnabled =
                    await this._trafficDeviationAccessService.isTrafficDeviationEnabled(
                        this.garageCode,
                    );

            });

        this._vehicleStore
            .getVehiclePropertyState(this.inventoryNumber, 'blockNumber')
            .pipe(takeUntil(this._ngUnsubscribeVehicle$))
            .subscribe(async (blockNumber) => {
                const value = blockNumber?.trim();

                if (!value) {
                    return;
                }

                if (this.signonForm.vehicletaskno !== value) {
                    this.signonForm = {
                        ...this.signonForm,
                        vehicletaskno: value,
                    };
                }

                await this.refreshSignOnTrips();
            });

        this._vehicleStore
            .getVehiclePropertyState(this.inventoryNumber, 'position')
            .pipe(takeUntil(this._ngUnsubscribeVehicle$))
            .subscribe((vehiclePosition) => {
                this._latestVehiclePosition = vehiclePosition;
                this._tryFollowVehicle(vehiclePosition);
                if (this._latestVehiclePosition) {
                    this._lineProgressStore.setPosition([
                        this._latestVehiclePosition.longitude,
                        this._latestVehiclePosition.latitude,
                    ]);
                }
            });

        // Listen to deviation changes
        this._vehicleStore
            .getVehiclePropertyState(this.inventoryNumber, 'departureDeviation')
            .pipe(takeUntil(this._ngUnsubscribeVehicle$))
            .subscribe((departureDeviation) => {
                // First set the departures from realtime
                if (
                    departureDeviation &&
                    (!this.departureHistory ||
                        this.departureHistory.length === 0)
                ) {
                    this.departureHistory = departureDeviation.departures;
                }

                // sets full list of departures from backend.
                this._setVehicleDepartureHistory(this.inventoryNumber);
            });

        // Listen to vehicle trip-changes in order to be able to show information about this
        this._vehicleStore
            .getVehiclePropertyState(this.inventoryNumber, 'tripNumber')
            .pipe(takeUntil(this._ngUnsubscribeVehicle$))
            .subscribe((vehicleTripNumber) => {
                setTimeout(() => {
                    this._handleTripChange(vehicleTripNumber);
                }, 100);
            });

        // Listen to vehicle warning changes
        this._vehicleStore
            .getVehiclePropertyState(this.inventoryNumber, 'warnings')
            .pipe(takeUntil(this._ngUnsubscribeVehicle$))
            .subscribe(() => {
                const warnings = this.vehicle?.displayableWarnings ?? [];
                this.warnings = warnings;

                const allConfirmable = _.filter(
                    warnings,
                    (item: VehicleWarning) =>
                        item.isActive === true &&
                        _.includes(
                            VehicleWarningConfig.confirmableWarnings,
                            item.messageType,
                        ),
                );

                if (this.isSignOnFeatureEnabled) {
                    // feature toggle ON for signon
                    // split signOn warnings to own list
                    this.signOnWarnings = _.filter(
                        allConfirmable,
                        (w) =>
                            w.messageType ===
                            WarningMessageType.SignOnMissingWarning,
                    );
                    this.confirmableWarnings = _.filter(
                        allConfirmable,
                        (w) =>
                            w.messageType !==
                            WarningMessageType.SignOnMissingWarning,
                    );
                } else {
                    // feature toggle OFF for signon
                    // let them just be confirmable
                    this.signOnWarnings = [];
                    this.confirmableWarnings = allConfirmable;
                }
            });
    }

    private async _setVehicleDepartureHistory(
        inventoryNumber,
    ): Promise<VehicleDepartureHistory> {
        const vehicleDepartureHistory: VehicleDepartureHistory =
            await firstValueFrom(
                this._vehicleInfoService.getVehicleDepartureHistory(
                    inventoryNumber,
                ),
            );

        if (vehicleDepartureHistory?.departures) {
            this.departureHistory = vehicleDepartureHistory.departures;
        }

        return vehicleDepartureHistory;
    }

    private _resetDataOnVehicleChange() {
        this._ngUnsubscribeVehicle$.next();
        this._ngUnsubscribeVehicle$.complete();
        this._ngUnsubscribeVehicle$ = new Subject();

        this._resetSignOnSelection();
        this.signonForm = {
            signOff: false,
            signOn: false,
            vehicletaskno: '',
            signOnItem: {
                vehicleactivityid: '',
                stopId: '',
                trafficDate: '',
                hastusregion: this.hastusRegion,
                routeName: '',
                tripNumber: '',
                sequenceNumber: '',
            },
            signOffItem: {
                trafficDate: '',
                hastusregion: this.hastusRegion,
                routeName: '',
                tripNumber: '',
            }
        };

        this.vehicleActivity$.next(undefined);
        this.blockActivites = undefined;
        this.departureHistory = undefined;
    }


    private _tryFollowVehicle(vehiclePosition: VehiclePosition) {
        if (this.followVehicle) {
            this._centerMapOnVehicle(vehiclePosition);
        }
    }

    private _centerMapOnVehicle(vehiclePosition: VehiclePosition) {
        if (vehiclePosition) {
            if (
                !isFinite(vehiclePosition.longitude) ||
                !isFinite(vehiclePosition.latitude)
            ) {
                this._loggingService.logEvent('InvalidLngLat_Vehicle', {
                    inventoryNumber: this.inventoryNumber,
                    tripNumber: this.vehicleActivity$.value?.tripNumber,
                    routePublicName:
                        this.vehicleActivity$.value?.routePublicName,
                    latitude: vehiclePosition.latitude,
                    longitude: vehiclePosition.longitude,
                });
                return;
            }
            this._mapboxService.flyTo([
                vehiclePosition.longitude,
                vehiclePosition.latitude,
            ]);
        }
    }

    private _selectVehicle() {
        this._vehicleStore.selectVehicles(
            [this.inventoryNumber],
            this._sourceComponentName,
        );
    }

    private _resetVehicleSelection() {
        this._vehicleStore.resetVehiclesSelection(this._sourceComponentName);
    }

    private _getVehicleComponentState(): VehicleComponentState {
        const storedState = localStorage.getItem('vehicleComponentState');
        const state = new VehicleComponentState();
        state.selectedTab = 'overview';
        state.tabs = ['overview', 'vehicleinfo', 'block'];

        if (this._authService.hasAccessToOmsFunction('OMS Map debug')) {
            state.tabs.push('debug');
        }

        // Restore tab only if reopening the same vehicle
        if (storedState) {
            const stored: VehicleComponentState = JSON.parse(storedState);
            if (
                stored.inventoryNumber === this.inventoryNumber &&
                _.includes(state.tabs, stored.selectedTab)
            ) {
                state.selectedTab = stored.selectedTab;
            }
        }
        state.inventoryNumber = this.inventoryNumber;
        return state;
    }

    private _updateVehicleComponentState() {
        localStorage.setItem(
            'vehicleComponentState',
            JSON.stringify(this.componentState),
        );
    }

    private async _handleTripChange(tripNumber: string) {
        this._clearMap();
        this.vehicleActivity$.next(undefined);

        if (tripNumber) {
            const response = await this._getTripInformation(tripNumber);
            if (response?.activityPoints?.length > 0) {
                this.vehicleActivity$.next(response);
                this._renderShapeAndStopsInMap();
            } else {
                this.vehicleActivity$.next(null);
                this._renderShapeAndStopsInMap();
            }
        }
    }

    private async _setVehicleActivites() {
        this.blockActivites = await this._getVehicleActivites(
            this.vehicle?.inventoryNumber,
            this.vehicle?.trafficDate,
        );
    }

    private _updateElecticVehicleTabs() {
        if (!this.componentState) {
            return;
        }
        if (
            this.vehicle?.isElectric &&
            _.find(this.componentState.tabs, (tab) => tab === 'soc') ===
            undefined
        ) {
            this.componentState.tabs.push('soc');
            this._updateVehicleComponentState();
        } else if (
            !this.vehicle?.isElectric &&
            _.find(this.componentState.tabs, (tab) => tab === 'soc') !==
            undefined
        ) {
            _.remove(this.componentState.tabs, (tab) => tab === 'soc');
            this.componentState.selectedTab = this.componentState.tabs[0];
            this._updateVehicleComponentState();
        }
    }

    private _updateVehicleSignOnTabs() {
        if (!this.componentState) {
            return;
        }

        const validGarageCodes = this._configService.signonValidGarageCodes;
        const garageCode = this.vehicle?.garageCode;
        const isValidGarage = validGarageCodes.includes(garageCode);
        const hasSignOn =
            _.find(this.componentState.tabs, (tab) => tab === 'signon') !==
            undefined;

        if (isValidGarage && !hasSignOn) {
            this.componentState.tabs.push('signon');
            this._updateVehicleComponentState();
        } else if (!isValidGarage && hasSignOn) {
            _.remove(this.componentState.tabs, (tab) => tab === 'signon');
            if (this.componentState.selectedTab === 'signon') {
                this.componentState.selectedTab = this.componentState.tabs[0];
            }
            this._updateVehicleComponentState();
        }
    }

    private async _getTripInformation(
        tripNumber: string,
    ): Promise<VehicleActivity> {
        const inventoryNumber = this.vehicle?.inventoryNumber;
        const routeName = this.vehicle?.routeNumber;

        try {
            return await firstValueFrom(
                this._vehicleInfoService.getVehicleActivity(
                    inventoryNumber,
                    routeName,
                    tripNumber,
                ),
            );
        } catch (error) {
            if (error.status === 404) {
                // eslint-disable-next-line max-len
                console.log(
                    `Vehicle activity for garage: ${this.garageCode}, routeName: ${routeName}, tripNumber: ${tripNumber} was not found`,
                );
            }
            return undefined;
        }
    }

    private async _getVehicleActivites(
        inventoryNumber: string,
        trafficDate?: string,
    ): Promise<VehicleBlockActivityInfo[]> {
        try {
            const block = await firstValueFrom(
                this._vehicleInfoService.getVehicleActivities(
                    inventoryNumber,
                    trafficDate,
                ),
            );

            // filtrera på inventoryNr eller blockNr (?)
            return block?.vehicleActivities || [];
        } catch (error) {
            return [];
        }
    }

    private _renderShapeAndStopsInMap() {
        // Update or remove shape
        const vehicleActivity = this.vehicleActivity$.value;
        if (vehicleActivity?.shapeGeometry) {
            const path: any = _.cloneDeep(
                vehicleActivity.shapeGeometry as turf.LineString,
            );
            this._lineProgressStore.setShape(path);
            if (this._latestVehiclePosition) {
                this._lineProgressStore.setPosition([
                    this._latestVehiclePosition.longitude,
                    this._latestVehiclePosition.latitude,
                ]);
            }
        } else {
            this._lineProgressStore.clear();
        }

        // Update or remove stops
        if (vehicleActivity?.activityPoints) {
            this._stopStore.setSelected(vehicleActivity.activityPoints);
        } else {
            this._stopStore.clearSelected();
        }
    }

    private _clearMap() {
        this._lineProgressStore.clear();
        this._stopStore.clearSelected();
    }

    public async submitSignOnUpdate(): Promise<void> {
        if (!this.isSignonSubmitEnabled || this.isSubmittingSignOnUpdate) {
            return;
        }

        this.isSubmittingSignOnUpdate = true;
        this.signOffStep = undefined;
        this.signOnStep = undefined;

        const selectedTrip = this.trips.find(
            (t) => t.vehicleactivityid === this.signonForm.signOnItem.vehicleactivityid
        );
        const trafficDate = this.vehicle?.trafficDate?.split('T')[0] ?? '';

        const payload = {
            inventoryNumber: this.vehicle?.inventoryNumber ?? '',
            signOff: this.signonForm.signOff,
            signOn: this.signonForm.signOn,
            vehicleTaskNo: this.signonForm.vehicletaskno ?? '',
            signOnItem: {
                trafficDate,
                hastusRegion: this.hastusRegion ?? '',
                routeName: selectedTrip?.route ?? '',
                tripNumber: selectedTrip?.tripNo ?? '',
                vehicleActivityId:
                    this.signonForm.signOnItem.vehicleactivityid ?? '',
                stopId: this.signonForm.signOnItem.stopId ?? '',
                sequenceNumber:
                    this.signonForm.signOnItem.sequenceNumber ?? '',
            },
            signOffItem: {
                trafficDate: this.currentAssignment?.trafficDate ?? trafficDate,
                hastusRegion: this.hastusRegion ?? '',
                routeName: this.currentAssignment?.routeName ?? '',
                tripNumber: this.currentAssignment?.tripNumber ?? '',
            },
        };

        try {
            const result = await firstValueFrom(
                this._warningsService.submitSignOnUpdate(payload),
            ) as SignOnFlowResult;
            this.signOffStep = result.signOff;
            this.signOnStep = result.signOn;
            this._resetSignOnSelection();
            await this.refreshSignOnTrips();

        } catch (error: unknown) {
            //borde aldrig hamna här pga att vi sväljer errors i servicen
            const message =
                typeof error === 'object' && error !== null && 'message' in error
                    ? String((error as { message?: string }).message)
                    : 'Failed to update sign-on status.';

            this._showMessage(message, true);
        } finally {
            this.isSubmittingSignOnUpdate = false;
        }
    }

    private _updateSignOnTrips(): void {
        if (!this.blockActivites || !this.vehicle?.blockNumber) {
            this.trips = [];
            return;
        }

        const now = Date.now();
        const trips = this.blockActivites.filter((a) => a.type === 'TRIPTRIP');

        const activeTripIndex = trips.findIndex((activity) => {
            const startTime = activity.startDateTime?.getTime();
            const endTime = activity.endDateTime?.getTime();

            if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
                return false;
            }

            return startTime !== undefined && endTime !== undefined && startTime <= now && now <= endTime;
        });

        const firstUpcomingTripIndex = trips.findIndex((activity) => {
            const startTime = activity.startDateTime?.getTime();
            return typeof startTime === 'number' && Number.isFinite(startTime) && startTime > now;
        });
        const selectedTripIndex =
            activeTripIndex !== -1 ? activeTripIndex : firstUpcomingTripIndex;

        if (selectedTripIndex === -1) {
            this.trips = [];
            return;
        }

        const visibleTrips = trips.slice(
            Math.max(0, selectedTripIndex - 1),
            selectedTripIndex + 2,
        );

        this.trips = visibleTrips.map((activity) => ({
            route: activity.routeName ?? '',
            tripNo: activity.tripNumber ?? '',
            time: (activity.startTime ?? '') + '-' + (activity.endTime ?? ''),
            designation: activity.tripDescription ?? '',
            vehicleactivityid: activity.vehicleActivityId ?? '',
        }));
    }

    private _resetSignOnSelection(): void {
        ++this._tripChangeRequestId;
        this.checkingTripState = false;
        this.loadingStops = false;
        const vehicletaskno = this.signonForm?.vehicletaskno ?? '';
        const hastusregion = this.hastusRegion ?? '';
        this.tripCurrentState = null;
        this.selectedSignOnTrip = undefined;

        this.trips = [];
        this.stops = [];
        this.signonForm = {
            signOff: false,
            signOn: false,
            vehicletaskno: vehicletaskno,
            signOnItem: {
                vehicleactivityid: '',
                stopId: '',
                trafficDate: '',
                hastusregion: hastusregion,
                routeName: '',
                tripNumber: '',
                sequenceNumber: '',
            },
            signOffItem: {
                trafficDate: '',
                hastusregion: hastusregion,
                routeName: '',
                tripNumber: '',
            }
        };
    }

    private _showMessage(message: string, isError = false): void {
        // replace with toast / snackbar / alert
        console.log(isError ? 'ERROR' : 'INFO', message);
    }

    private async refreshSignOnTrips(): Promise<void> {
        if (!this.vehicle?.inventoryNumber) {
            this.trips = [];
            return;
        }

        this.blockActivites = await this._getVehicleActivites(
            this.vehicle.inventoryNumber,
            this.vehicle.trafficDate,
        );

        this._updateSignOnTrips();
    }
}
